import { Datum } from "./datastructure";
import {
  getSpecialFolderType,
  isProcessingFolder,
  ReadOnlyContext,
  SpecialFolderType,
} from "./specialFolders";

/**
 * Gets the display name for a datum.
 * For synthetic drive folders, uses driveName from payload.
 * Otherwise uses the path-derived name.
 */
export const getDisplayName = (datum: Datum): string => {
  if (datum.payload?.type === "folder" && datum.payload.driveName) {
    return datum.payload.driveName;
  }
  return datum.name || "";
};

const syncFolderPriorityMap: Record<NonNullable<SpecialFolderType>, number> = {
  "read-only-drive": 1,
  "ms-onenote": 2,
  "ms-sharepoint": 3,
  "ms-teams": 4,
  box: 5,
  "google-drive": 6,
};

/**
 * Gets the sync folder priority (lower number = higher priority).
 * Uses getSpecialFolderType from specialFolders.ts for detection.
 */
export const getSyncFolderPriority = (
  datum: Datum,
  ctx?: ReadOnlyContext
): number => {
  if (datum.type !== "folder") return 999;
  // The synthetic Processing folder pins above everything else (#11257): it is
  // the most transient thing in the tree and the thing a user checks during an
  // ingestion, so it should not have to be hunted for alphabetically. Checked
  // here rather than by adding it to SpecialFolderType, because
  // getSpecialFolderType matches on the root path segment and would then
  // classify the folder's *contents* as special-folder descendants — which
  // would make them read-only, and they are deliberately still editable.
  if (isProcessingFolder(datum, ctx)) return 0;
  const folderType = getSpecialFolderType(datum.path, ctx);
  if (!folderType) return 999;
  return syncFolderPriorityMap[folderType];
};

/**
 * Gets the sort date for a file given its date fields.
 * Uses documentDate if valid and not in the future, otherwise uploadedAt.
 */
export const getFileSortDate = (
  documentDate: string | null | undefined,
  uploadedAt: string | null | undefined
): number => {
  if (documentDate) {
    const docDate = new Date(documentDate).getTime();
    if (docDate <= Date.now()) {
      return docDate;
    }
  }
  return uploadedAt ? new Date(uploadedAt).getTime() : 0;
};

/**
 * Gets the date for sorting a datum.
 * For files: uses documentDate or uploadedAt.
 * For folders: recursively finds the most recent date from all descendants.
 */
export const getSortDate = (datum: Datum): number => {
  if (datum.type === "file" && datum.payload?.type === "file") {
    return getFileSortDate(
      datum.payload.documentDate,
      datum.payload.uploadedAt
    );
  }

  // Folder: find most recent child date recursively
  if (!datum.children) return 0;
  let maxDate = 0;
  for (const child of datum.children) {
    maxDate = Math.max(maxDate, getSortDate(child));
  }
  return maxDate;
};

/**
 * Sorting function for file tree items
 * Order: sync folders (by priority), regular folders, files (sorted by selected column)
 */
export const sortFileTreeItems = (
  a: Datum,
  b: Datum,
  sortBy: "name" | "date" = "name",
  sortDirection: "asc" | "desc" = "asc",
  ctx?: ReadOnlyContext
): number => {
  // 1. Type-based sorting (folders before files)
  const typeOrder: Record<Datum["type"], number> = { folder: 0, file: 1 };
  const typeDiff = typeOrder[a.type] - typeOrder[b.type];
  if (typeDiff !== 0) return typeDiff;

  // 2. For folders: sync folders first, then regular folders
  if (a.type === "folder" && b.type === "folder") {
    const aSyncPriority = getSyncFolderPriority(a, ctx);
    const bSyncPriority = getSyncFolderPriority(b, ctx);
    const syncDiff = aSyncPriority - bSyncPriority;
    if (syncDiff !== 0) return syncDiff;
  }

  // 3. Sort by selected column
  let cmp: number;
  if (sortBy === "date") {
    const aDate = getSortDate(a);
    const bDate = getSortDate(b);

    // Push items without dates to the bottom (regardless of sort direction)
    if (aDate === 0 && bDate !== 0) return 1;
    if (aDate !== 0 && bDate === 0) return -1;

    cmp = aDate - bDate;
  } else {
    cmp = getDisplayName(a).localeCompare(getDisplayName(b));
  }

  // Apply direction
  if (sortDirection === "desc") {
    cmp = -cmp;
  }

  // 4. Fall back to name then ID for stable sorting
  if (cmp !== 0) return cmp;
  if (sortBy === "date") {
    const nameCmp = getDisplayName(a).localeCompare(getDisplayName(b));
    if (nameCmp !== 0) return nameCmp;
  }
  return a.id.localeCompare(b.id);
};

/**
 * Applies sorting to all children in the datum tree
 */
export const sortFileTree = (
  datums: Datum[],
  sortBy: "name" | "date" = "name",
  sortDirection: "asc" | "desc" = "asc",
  ctx?: ReadOnlyContext
): void => {
  for (const datum of datums) {
    if (!datum.children) continue;
    datum.children.sort((a, b) =>
      sortFileTreeItems(a, b, sortBy, sortDirection, ctx)
    );
  }
};
